/**
 * Framework-agnostic mount crash reporter.
 *
 * When a component mount fails after its retry budget, the
 * core pipeline swaps the container contents for an inline
 * error UI, persists the crash record into sessionStorage for
 * sdx_devel to surface after the next navigation, and
 * dispatches a `sdx:mount-error` DOM event for live listeners
 * (the devel toolbar, integration tests, etc.).
 *
 * Persistence uses sessionStorage because a full page load
 * (error recovery via navigate fallback) must not lose the
 * error trail but the records do not need to survive across
 * sessions.
 */

const SESSION_STORAGE_KEY = 'sdx_mount_errors';
const MOUNT_ERROR_EVENT = 'sdx:mount-error';

export interface CrashReporterContext {
  logPrefix: string;
}

export function reportMountCrash(
  container: HTMLElement,
  componentId: string,
  error: unknown,
  retryCount: number,
  ctx: CrashReporterContext,
): void {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? error.stack ?? '' : '';

  console.error(`${ctx.logPrefix} Failed to mount ${componentId}:`, error);

  renderInlineError(container, componentId, message);
  persistCrashRecord(componentId, message, stack);

  document.dispatchEvent(
    new CustomEvent(MOUNT_ERROR_EVENT, {
      detail: {
        componentId,
        error: message,
        stack,
        retry: retryCount,
      },
    }),
  );
}

function renderInlineError(container: HTMLElement, componentId: string, message: string): void {
  const errDiv = document.createElement('div');
  errDiv.className = 'sdx-mount-error';
  const strong = document.createElement('strong');
  strong.textContent = `Component Error: ${componentId}`;
  errDiv.appendChild(strong);
  errDiv.appendChild(document.createElement('br'));
  errDiv.appendChild(document.createTextNode(message));
  container.textContent = '';
  container.appendChild(errDiv);
  container.dataset.sdxMounted = 'error';
}

function persistCrashRecord(componentId: string, message: string, stack: string): void {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const existing = raw ? (JSON.parse(raw) as unknown[]) : [];
    if (!Array.isArray(existing)) return;
    existing.push({
      componentId,
      error: message,
      stack: stack.split('\n').slice(0, 5).join('\n'),
      time: Date.now(),
    });
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(existing));
  }
  catch {
    // Storage quota or serialization errors are non-fatal; the
    // DOM event still surfaces the crash to sdx_devel.
  }
}
